import { default as React, useEffect, useState } from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';

export interface ServiceOffering {
  name: string;
  number: string;
  operationalStatus?: string | null;
  shortDescription?: string | null;
}

const IopServiceOfferingsQuery = gql`
  query GetIopServiceOfferings {
    resources {
      iopServiceOfferings {
        name
        number
        operationalStatus
        shortDescription
      }
    }
  }
`;

interface Props {
  className?: string;
  helperText?: string;
  label?: string;
  multiple?: boolean;
  selectedServiceOffering?: ServiceOffering | null;
  setSelectedServiceOffering?: React.Dispatch<
    React.SetStateAction<ServiceOffering | null>
  >;
  selectedServiceOfferings?: ServiceOffering[];
  setSelectedServiceOfferings?: React.Dispatch<
    React.SetStateAction<ServiceOffering[]>
  >;
  required?: boolean;
}

/**
 * Dropdown selector for IOP Service Offerings.
 */
export function ServiceOfferingSelect({
  className,
  helperText = 'Select a service offering',
  label = 'Service Offering',
  multiple = false,
  selectedServiceOffering,
  setSelectedServiceOffering,
  selectedServiceOfferings = [],
  setSelectedServiceOfferings,
  required = false,
}: Props) {
  const [serviceOfferings, setServiceOfferings] = useState<ServiceOffering[]>(
    [],
  );

  const [getServiceOfferings, { loading, error }] = useLazyQuery(
    IopServiceOfferingsQuery,
    {
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    getServiceOfferings({})
      .then(result => {
        const data: ServiceOffering[] | undefined =
          result?.data?.resources?.iopServiceOfferings;
        if (data) {
          setServiceOfferings(data);
        }
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching service offerings', err);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (
    _event: Object,
    value: ServiceOffering | ServiceOffering[] | null,
    _reason: DevHubAutocompleteChangeReason,
  ) => {
    if (multiple) {
      if (setSelectedServiceOfferings) {
        setSelectedServiceOfferings(Array.isArray(value) ? value : []);
      }
      return;
    }

    if (setSelectedServiceOffering) {
      setSelectedServiceOffering(
        !Array.isArray(value) ? value : (value[0] ?? null),
      );
    }
  };

  if (error) {
    return (
      <DevHubTextField
        className={className}
        error
        label="Error"
        defaultValue="No Service Offerings Available"
        variant="outlined"
      />
    );
  }

  return (
    <DevHubAutocomplete
      multiple={multiple}
      className={className}
      loading={loading}
      loadingText="Loading.."
      options={serviceOfferings}
      value={multiple ? selectedServiceOfferings : selectedServiceOffering}
      onChange={handleChange}
      getOptionLabel={(option: ServiceOffering) =>
        option.number ? `${option.name} (${option.number})` : option.name
      }
      getOptionSelected={(option: ServiceOffering, value: ServiceOffering) =>
        option.number === value.number
      }
      renderInput={params => (
        <DevHubTextField
          {...params}
          label={label}
          helperText={helperText}
          margin="normal"
          variant="outlined"
          required={required}
          InputProps={params.InputProps}
        />
      )}
    />
  );
}
